import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import nls from '../../../../nls/pt-BR.json';
import { withHookFormMask } from 'use-mask-input';
import {
  labelFormatterMoneyAsInteger,
  unmaskAbsoluteValue,
  unmaskPercentile,
} from '../../../../../../../shared/formatters/formatters';
import {
  calculateInstallmentsSchema,
  InstallmentsItems,
  InstallmentsSimulation,
  LayoutInstallmentsFormData,
  LayoutValuationResponse,
  Step,
} from '../../../../redux/studies.model';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch } from 'react-redux';
import {
  advanceToStep,
  setInstallmentsSimulationPrimary,
  setInstallmentsSimulationSecondary,
  setQualifiedAudience,
  studiesSelector,
} from '../../../../redux/studies.reducer';
import { setIsLoadingMap } from '../../../../../redux/dashboardActions';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';
import { ExitGuideButton, PreviousStepButton } from '../../components';
import { postStudyStep } from '../../../../redux/studies.controller';

interface ResultItems {
  installmentValue: string;
  unitTotalValue: string;
  interestValue: string;
}

interface Result {
  [key: number]: ResultItems | null;
}

interface FormItemProps {
  installmentIndex: number;
  area: number;
  results: Result | null;
  setResults: Dispatch<SetStateAction<Result | null>>;
  data: LayoutValuationResponse;
}
interface FormProps {
  data: LayoutValuationResponse;
  currentStep: Step;
  marketType: 'primary' | 'secondary';
}

const generatePayload = (
  data: LayoutInstallmentsFormData,
  results: Result,
  marketType: 'primary' | 'secondary',
  layouts: LayoutValuationResponse | null
) => {
  const payload = data.installments.map((elem, index) => ({
    id: layouts?.[index]['id'],
    ...elem,
    ...results[index],
    marketType,
  }));
  const formattedPayload = payload.map((elem, index) => ({
    id: elem.id,
    ...unmaskValues({ ...elem }, false),
    marketType,
  }));
  const unformattedPayload = payload.map((elem, index) => ({
    id: elem.id,
    ...unmaskValues({ ...elem }, true),
    marketType,
  }));
  return { formattedPayload, unformattedPayload };
};

export const CalculateLayoutInstallmentsForm: React.FC<FormProps> = ({
  data,
  currentStep,
  marketType,
}) => {
  const { layoutsValuationSecondaryResponse, id, expandedArea } =
    useTypedSelector(studiesSelector);
  const { geocodePayload } = useTypedSelector(
    (state) => state.socioeconomicDemographicState
  );
  const [results, setResults] = useState<Result | null>(null);

  const getDefaultValues = () => {
    const prices = data.map((valuation, index) => ({
      unitValue: labelFormatterMoneyAsInteger(valuation.pricePerUnit),
      downPaymentPercentile: undefined,
      numberOfInstallments: undefined,
      monthlyInterestPercentile: undefined,
    }));
    return prices;
  };

  const methods = useForm<LayoutInstallmentsFormData>({
    resolver: zodResolver(calculateInstallmentsSchema),
    defaultValues: {
      installments: getDefaultValues(),
    },
  });
  const { fields } = useFieldArray<LayoutInstallmentsFormData>({
    control: methods.control,
    name: 'installments',
  });
  const dispatch = useDispatch();

  const onSubmit = async (formData: LayoutInstallmentsFormData) => {
    try {
      const { formattedPayload, unformattedPayload } = generatePayload(
        formData,
        results as Result,
        marketType,
        data
      );
      dispatch(setIsLoadingMap(true));
      const customHeader = expandedArea ? { pagination: 'true' } : null;
      const payload = {
        layouts: formattedPayload,
        sector_ids: geocodePayload,
        expandedArea: expandedArea
          ? {
              center: [
                expandedArea?.getCenter()?.lat(),
                expandedArea?.getCenter()?.lng(),
              ],
              radius: expandedArea?.getRadius(),
            }
          : null,
      };
      const response = await postStudyStep(5, id, payload, customHeader);
      if (marketType === 'primary')
        dispatch(
          setInstallmentsSimulationPrimary(
            unformattedPayload as InstallmentsSimulation[]
          )
        );
      if (marketType === 'secondary')
        dispatch(
          setInstallmentsSimulationSecondary(
            unformattedPayload as InstallmentsSimulation[]
          )
        );
      dispatch(setQualifiedAudience(response.perfil_socio));
      if (layoutsValuationSecondaryResponse) {
        dispatch(advanceToStep(currentStep.nextStep));
      } else {
        dispatch(advanceToStep('qualifiedAudienceResponse'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsLoadingMap(false));
    }
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {fields.map((fieldData, index) => {
          return (
            <CalculateLayoutInstallmentsFormItem
              installmentIndex={index}
              area={data[index].area}
              key={fieldData.id}
              results={results}
              setResults={setResults}
              data={data}
            />
          );
        })}
        <div className="study-creation-guide__buttons-container">
          <div>
            <ExitGuideButton />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <PreviousStepButton />
            <button
              className="btn normal primary"
              data-cy="StudyCreationGuideNextStep"
              type="submit"
            >
              {nls.studyCreationGuide.buttons.forward}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const unmaskValues = (
  {
    downPaymentPercentile,
    monthlyInterestPercentile,
    numberOfInstallments,
    unitValue,
    installmentValue,
    unitTotalValue,
    interestValue,
  }: any,
  returnAsString: boolean
) => {
  const unsmaskedDownPayment = unmaskPercentile(downPaymentPercentile);
  const unmaskedInterest = unmaskPercentile(monthlyInterestPercentile);
  const unmaskedUnitValue = unmaskAbsoluteValue(unitValue.split('R$')[1]);
  const unmaskedTotalValue = unmaskAbsoluteValue(
    unitTotalValue?.split('R$')[1]
  );
  const unmaskedInstallmentValue = unmaskAbsoluteValue(
    installmentValue.split('R$')[1]
  );
  const unmaskedTotalInterest = unmaskAbsoluteValue(
    interestValue.split('R$')[1]
  );
  if (returnAsString) {
    return {
      unitValue: unmaskedUnitValue.toFixed(0),
      downPaymentPercentile: unsmaskedDownPayment.toFixed(0),
      numberOfInstallments: numberOfInstallments.toFixed(0),
      monthlyInterestPercentile: unmaskedInterest.toString(),
      installmentValue: parseInt(unmaskedInstallmentValue.toFixed(0)),
      unitTotalValue: parseInt(unmaskedTotalValue.toFixed(0)),
      interestValue: parseInt(unmaskedTotalInterest.toFixed(0)),
    };
  }

  return {
    unitValue: unmaskedUnitValue.toFixed(0),
    downPaymentPercentile: parseInt(unsmaskedDownPayment.toFixed(0)),
    numberOfInstallments: parseInt(numberOfInstallments.toFixed(0)),
    monthlyInterestPercentile: unmaskedInterest,
    installmentValue: parseInt(unmaskedInstallmentValue.toFixed(0)),
    unitTotalValue: parseInt(unmaskedTotalValue.toFixed(0)),
    interestValue: parseInt(unmaskedTotalInterest.toFixed(0)),
  };
};

const CalculateLayoutInstallmentsFormItem: React.FC<FormItemProps> = ({
  installmentIndex,
  area,
  results,
  setResults,
  data,
}) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<LayoutInstallmentsFormData>();

  const handleBlur = () => {
    const currentValues = watch(`installments.${installmentIndex}`);
    const hasUndefinedValues = Object.values(currentValues).some(
      (fieldValue) => !!fieldValue === false
    );

    if (hasUndefinedValues)
      return setResults({ ...results, [installmentIndex]: null });
    const {
      downPaymentPercentile,
      monthlyInterestPercentile,
      numberOfInstallments,
      unitValue,
    } = currentValues;
    const unsmaskedDownPayment = unmaskPercentile(downPaymentPercentile);
    const unmaskedInterest = unmaskPercentile(monthlyInterestPercentile);
    const unmaskedUnitValue = unmaskAbsoluteValue(unitValue.split('R$')[1]);

    const downPayment = (unsmaskedDownPayment / 100) * unmaskedUnitValue;
    const valueMinusDownpayment = unmaskedUnitValue - downPayment;

    const totalValue =
      valueMinusDownpayment *
        (1 + unmaskedInterest / 100) ** numberOfInstallments +
      downPayment;
    const totalInterest = totalValue - unmaskedUnitValue;
    const installmentValue =
      (valueMinusDownpayment + totalInterest) / numberOfInstallments;
    const calcRes = {
      installmentValue: labelFormatterMoneyAsInteger(installmentValue),
      unitTotalValue: labelFormatterMoneyAsInteger(totalValue),
      interestValue: labelFormatterMoneyAsInteger(totalInterest),
    };
    return setResults({ ...results, [installmentIndex]: calcRes });
  };

  const hasErrors = (inputName: keyof InstallmentsItems) => {
    return errors?.installments?.[installmentIndex]?.[inputName];
  };
  const downPaymentsHookMask: any = {
    ...withHookFormMask(
      register(
        `installments.${installmentIndex}.downPaymentPercentile` as const
      ),
      ['9%', '99%', '9,9%', '9,99%', '99,99%']
    ),
  };
  const interestHookMask: any = {
    ...withHookFormMask(
      register(`installments.${installmentIndex}.monthlyInterestPercentile`),
      ['9%', '99%', '9,9%', '9,99%', '99,99%']
    ),
  };
  return (
    <div className="installments-form-container">
      <div className="row-container header" style={{ margin: 0 }}>
        <h6 className="layout-number" style={{ margin: '2px 0px 4px 0px' }}>
          {nls.studyCreationGuide.steps.step5.form.layoutNumber}{' '}
          {data?.[installmentIndex]['id']}
        </h6>
        <span style={{ fontWeight: 700, fontSize: 14 }}>
          {area}
          {nls.studyCreationGuide.steps.step5.form.sqMt}
        </span>
      </div>
      <div className="installments-form-container__grid-container">
        <div>
          <label htmlFor={`installments.${installmentIndex}.unitValue`}>
            {nls.studyCreationGuide.steps.step5.form.unitValue}
          </label>
          <input
            className="default-input disabled"
            {...register(`installments.${installmentIndex}.unitValue`)}
            disabled
            id={`installments.${installmentIndex}.unitValue`}
          />
        </div>
        <div>
          <label
            htmlFor={`installments.${installmentIndex}.downPaymentPercentile`}
          >
            {nls.studyCreationGuide.steps.step5.form.downPayment}
          </label>
          <input
            className={`default-input ${
              hasErrors('downPaymentPercentile') ? 'error' : ''
            }`}
            {...downPaymentsHookMask}
            id={`installments.${installmentIndex}.downPaymentPercentile`}
            onBlur={handleBlur}
            autoComplete="off"
          />
        </div>

        <div>
          <label
            htmlFor={`installments.${installmentIndex}.numberOfInstallments`}
          >
            {nls.studyCreationGuide.steps.step5.form.numberOfInstallments}
          </label>
          <input
            className={`default-input ${
              hasErrors('numberOfInstallments') ? 'error' : ''
            }`}
            type="number"
            {...register(
              `installments.${installmentIndex}.numberOfInstallments`,
              { valueAsNumber: true }
            )}
            id={`installments.${installmentIndex}.numberOfInstallments`}
            onBlur={handleBlur}
            autoComplete="off"
          />
        </div>

        <div>
          <label
            htmlFor={`installments.${installmentIndex}.monthlyInterestPercentile`}
          >
            {nls.studyCreationGuide.steps.step5.form.monthlyInterest}
          </label>
          <input
            className={`default-input ${
              hasErrors('monthlyInterestPercentile') ? 'error' : ''
            }`}
            {...interestHookMask}
            id={`installments.${installmentIndex}.monthlyInterestPercentile`}
            onBlur={handleBlur}
            autoComplete="off"
          />
        </div>
      </div>
      <div>
        <div className="row-container results">
          <p>{nls.studyCreationGuide.steps.step5.form.installmentValue}</p>
          <p style={{ fontWeight: 700 }}>
            {results?.[installmentIndex]?.installmentValue
              ? results?.[installmentIndex]?.installmentValue
              : '-'}
          </p>
        </div>
        <div className="row-container results">
          <p>{nls.studyCreationGuide.steps.step5.form.unitTotalValue}</p>
          <p style={{ fontWeight: 700 }}>
            {results?.[installmentIndex]?.unitTotalValue
              ? results?.[installmentIndex]?.unitTotalValue
              : '-'}
          </p>
        </div>
        <div className="row-container results">
          <p>{nls.studyCreationGuide.steps.step5.form.interestValue}</p>
          <p style={{ fontWeight: 700 }}>
            {results?.[installmentIndex]?.interestValue
              ? results?.[installmentIndex]?.interestValue
              : '-'}
          </p>
        </div>
      </div>
    </div>
  );
};
